// Central API helpers for all Phase 1 entities.
import { api } from "./api";

// RC10f · Unit Types catalog (Correction B). Dynamic replacement
// for the hardcoded ASSET_TYPES/LEGACY_ASSET_TYPES arrays that used
// to live in AssetFormDialog + FleetList.
export const unitTypeApi = {
  list: (params = {}) => api.get("/unit-types", { params }).then((r) => r.data),
  listActive: () =>
    api.get("/unit-types", { params: { active_only: true } }).then((r) => r.data),
  create: (body) => api.post("/unit-types", body).then((r) => r.data),
  patch: (id, body) => api.patch(`/unit-types/${id}`, body).then((r) => r.data),
  archive: (id) => api.delete(`/unit-types/${id}`).then((r) => r.data),
  reactivate: (id) =>
    api.post(`/unit-types/${id}/reactivate`).then((r) => r.data),
};

export const orgApi = {
  list: (params = {}) => api.get("/organizations", { params }).then((r) => r.data),
  get: (id) => api.get(`/organizations/${id}`).then((r) => r.data),
  create: (body) => api.post("/organizations", body).then((r) => r.data),
  patch: (id, body) => api.patch(`/organizations/${id}`, body).then((r) => r.data),
  archive: (id) => api.delete(`/organizations/${id}`).then((r) => r.data),  // Phase A.2.a canonical reverse view — Contacts linked to this Organization.
  contacts: (id, params = {}) =>
    api.get(`/organizations/${id}/contacts`, { params }).then((r) => r.data),
};

export const contactApi = {
  list: (params = {}) => api.get("/contacts", { params }).then((r) => r.data),
  // Phase A.1 canonical global Contact search. Supports optional
  // context filters `exclude_linked_to_customer_id`, `exclude_linked_to_tour_id`,
  // `exclude_linked_to_organization_id`. Enriched result rows carry
  // `linked_client_ids`, `linked_tour_ids`, `linked_organization_ids`,
  // and existing-relationship indicators when a context is applied.
  search: (params = {}) =>
    api.get("/contacts/search", { params }).then((r) => r.data),
  // RC10 Phase 2C · duplicate-check for create/edit workflows.
  // Returns safe-summary candidate list capped at 10. Never mutates.
  dupCheck: (params = {}) =>
    api.get("/contacts/dup-check", { params }).then((r) => r.data),
  get: (id) => api.get(`/contacts/${id}`).then((r) => r.data),
  create: (body) => api.post("/contacts", body).then((r) => r.data),
  patch: (id, body) => api.patch(`/contacts/${id}`, body).then((r) => r.data),
  archive: (id) => api.delete(`/contacts/${id}`).then((r) => r.data),
  // CC1 · Contact → Customer promotion. Idempotent — a second call returns
  // { already_promoted: true, customer } instead of creating a duplicate.
  promoteToCustomer: (id) =>
    api.post(`/contacts/${id}/promote-to-customer`).then((r) => r.data),
  // Phase A.2.a canonical reverse view — Tours this Contact is linked to.
  tours: (id, params = {}) =>
    api.get(`/contacts/${id}/tours`, { params }).then((r) => r.data),
};

export const contactRoleApi = {
  attach: (body) => api.post("/contact-organization-roles", body).then((r) => r.data),
  // CC1 · Edit the Contact Type (role) on an existing ContactOrgRole.
  patch: (id, body) =>
    api.patch(`/contact-organization-roles/${id}`, body).then((r) => r.data),
  detach: (id) => api.delete(`/contact-organization-roles/${id}`).then((r) => r.data),
};

// CC1 · Client↔Contact relationships (bidirectional).
export const clientContactRelApi = {
  forClient: (customerId) =>
    api.get(`/customers/${customerId}/contact-relationships`).then(r => r.data),
  forContact: (contactId) =>
    api.get(`/contacts/${contactId}/client-relationships`).then(r => r.data),
  create: (payload) =>
    api.post(`/client-contact-relationships`, payload).then(r => r.data),
  patch: (id, payload) =>
    api.patch(`/client-contact-relationships/${id}`, payload).then(r => r.data),
  unlink: (id) =>
    api.delete(`/client-contact-relationships/${id}`).then(r => r.data),
  searchClients: (q, limit = 20) =>
    api.get(`/clients/search`, { params: { q, limit } }).then(r => r.data),
  searchContacts: (q, limit = 20) =>
    api.get(`/contacts-search`, { params: { q, limit } }).then(r => r.data),
};

// CC1 · Legacy StarTracker workbook import.
export const legacyImportApi = {
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/imports/legacy/upload", fd).then((r) => r.data);
  },
  get: (batchId) => api.get(`/imports/legacy/${batchId}`).then((r) => r.data),
  list: (params = {}) => api.get("/imports/legacy", { params }).then((r) => r.data),
  apply: (batchId) =>
    api.post(`/imports/legacy/${batchId}/apply`).then((r) => r.data),
  // CC1 Review Completion — preview-only mutations. Zero domain writes.
  dashboard: (batchId) =>
    api.get(`/imports/legacy/${batchId}/dashboard`).then((r) => r.data),
  orgNearDuplicates: (batchId) =>
    api.get(`/imports/legacy/${batchId}/org-near-duplicates`).then((r) => r.data),
  editProposal: (batchId, sourceRow, patch) =>
    api.patch(`/imports/legacy/${batchId}/proposals/${sourceRow}`, patch).then((r) => r.data),
  classifyBusinessMgr: (batchId, sourceRow, decision) =>
    api.post(`/imports/legacy/${batchId}/proposals/${sourceRow}/classify`, { decision }).then((r) => r.data),
  resolveDuplicate: (batchId, sourceRow, action, contactRoleInRow) =>
    api.post(
      `/imports/legacy/${batchId}/proposals/${sourceRow}/resolve-duplicate`,
      { action, contact_role_in_row: contactRoleInRow },
    ).then((r) => r.data),
  bulkApproveReady: (batchId) =>
    api.post(`/imports/legacy/${batchId}/bulk-approve-ready`).then((r) => r.data),
  refreshMatches: (batchId) =>
    api.post(`/imports/legacy/${batchId}/refresh-matches`).then((r) => r.data),
};

export const customerApi = {
  list: (params = {}) => api.get("/customers", { params }).then((r) => r.data),
  get: (id) => api.get(`/customers/${id}`).then((r) => r.data),
  create: (body) => api.post("/customers", body).then((r) => r.data),
  patch: (id, body) => api.patch(`/customers/${id}`, body).then((r) => r.data),
  archive: (id) => api.delete(`/customers/${id}`).then((r) => r.data),
  // CC2 · Customer Notes ledger. Newest first. Server-scoped.
  notesList: (id, params = {}) =>
    api.get(`/customers/${id}/notes`, { params }).then((r) => r.data),
  notesAdd: (id, body) =>
    api.post(`/customers/${id}/notes`, { body }).then((r) => r.data),
  // CC2 · Customer Activity ledger — permission-gated on `customers.view_activity`.
  activity: (id, params = {}) =>
    api.get(`/customers/${id}/activity`, { params }).then((r) => r.data),
};

export const assetApi = {
  list: (params = {}) => api.get("/assets", { params }).then((r) => r.data),
  get: (id) => api.get(`/assets/${id}`).then((r) => r.data),
  create: (body) => api.post("/assets", body).then((r) => r.data),
  patch: (id, body, opts) =>
    api
      .patch(
        `/assets/${id}${opts?.confirm_transfer ? "?confirm_transfer=true" : ""}`,
        body,
      )
      .then((r) => r.data),
  archive: (id) => api.delete(`/assets/${id}`).then((r) => r.data),
  changeStatus: (id, to_status, reason) =>
    api.post(`/assets/${id}/status`, { to_status, reason }).then((r) => r.data),
  history: (id) => api.get(`/assets/${id}/status-history`).then((r) => r.data),
  uploadPhoto: (id, file, setPrimary = false) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/assets/${id}/photos?set_primary=${setPrimary}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
};

export const driverApi = {
  list: (params = {}) => api.get("/drivers", { params }).then((r) => r.data),
  get: (id) => api.get(`/drivers/${id}`).then((r) => r.data),
  me: () => api.get("/drivers/me").then((r) => r.data),
  create: (body) => api.post("/drivers", body).then((r) => r.data),
  patch: (id, body) => api.patch(`/drivers/${id}`, body).then((r) => r.data),
  archive: (id) => api.delete(`/drivers/${id}`).then((r) => r.data),
  // DAQ-1 Sub-Phase 3 · Eligibility-aware search. Returns
  // `{total, page, page_size, results:[{driver, eligibility}], applied_context}`.
  search: (params = {}) => api.get("/drivers/search", { params }).then((r) => r.data),
};

// DAQ-1 Sub-Phase 2/3 · Driver availability windows CRUD.
export const driverAvailabilityApi = {
  list: (driverId) =>
    api.get(`/drivers/${driverId}/availability-windows`).then((r) => r.data),
  create: (driverId, body) =>
    api.post(`/drivers/${driverId}/availability-windows`, body).then((r) => r.data),
  patch: (driverId, windowId, body) =>
    api.patch(`/drivers/${driverId}/availability-windows/${windowId}`, body).then((r) => r.data),
  archive: (driverId, windowId) =>
    api.delete(`/drivers/${driverId}/availability-windows/${windowId}`).then((r) => r.data),
  // RC10p — Batched read for the Schedule page. Returns
  // `[{driver_id, start_date, end_date, status, reason}]` for every
  // non-archived window overlapping [start, end].
  windowsBatch: ({ start, end }) =>
    api.get(`/driver-availability/windows`, { params: { start, end } }).then((r) => r.data),
};

// DAQ-1 Sub-Phase 2/3 · Driver ↔ Client (artist) DO NOT ASSIGN restrictions.
export const driverArtistRestrictionApi = {
  list: (driverId) =>
    api.get(`/drivers/${driverId}/artist-restrictions`).then((r) => r.data),
  create: (driverId, body) =>
    api.post(`/drivers/${driverId}/artist-restrictions`, body).then((r) => r.data),
  patch: (driverId, restrictionId, body) =>
    api.patch(`/drivers/${driverId}/artist-restrictions/${restrictionId}`, body).then((r) => r.data),
  archive: (driverId, restrictionId) =>
    api.delete(`/drivers/${driverId}/artist-restrictions/${restrictionId}`).then((r) => r.data),
};

export const documentApi = {
  // Accepts either the new object form `{ entity_type, entity_id, limit }`
  // or the legacy positional form (entity_type, entity_id) still used by
  // CustomerDetail.
  list: (a = {}, b) => {
    const params =
      typeof a === "string"
        ? { entity_type: a, entity_id: b }
        : a;
    return api.get("/documents", { params }).then((r) => r.data);
  },
  get: (id) => api.get(`/documents/${id}`).then((r) => r.data),
  upload: ({ file, title, document_type, entity_type, entity_id, notes }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("document_type", document_type);
    fd.append("entity_type", entity_type);
    fd.append("entity_id", entity_id);
    if (notes) fd.append("notes", notes);
    return api
      .post("/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  archive: (id) => api.delete(`/documents/${id}`).then((r) => r.data),
  // Legacy — CustomerDetail passes a raw file_id + download_token and
  // builds the /files/<file_id> URL directly. Left untouched.
  downloadUrl: (fileId, token) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/files/${fileId}?token=${encodeURIComponent(token)}`;
  },
  // Preferred — resolve a fresh signed download URL for a Document by
  // its record id. Backend returns a short-lived signed download_token
  // scoped to the underlying file_id.
  signedUrl: async (docId) => {
    const payload = await api.get(`/documents/${docId}`).then((r) => r.data);
    if (!payload?.file_id || !payload?.download_token) throw new Error("no_signed_url");
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/files/${payload.file_id}?token=${encodeURIComponent(payload.download_token)}`;
  },
};

export const searchApi = {
  global: (q) => api.get("/search", { params: { q } }).then((r) => r.data),
};

export const auditApi = {
  forEntity: (entity_type, entity_id, limit = 50) =>
    api
      .get("/audit-logs", { params: { entity_type, entity_id, limit } })
      .then((r) => r.data),
};


// RC10h · Asset Owner helpers (canonical Organization w/ org_type in
// coach_owner/trailer_owner + ContactOrgRole links joined live).
export const assetOwnerApi = {
  list: (params = {}) =>
    api.get("/asset-owners", { params }).then((r) => r.data),
  get: (id) => api.get(`/asset-owners/${id}`).then((r) => r.data),
  create: (body) => api.post("/asset-owners", body).then((r) => r.data),
  patch: (id, body) =>
    api.patch(`/asset-owners/${id}`, body).then((r) => r.data),
  archive: (id) => api.delete(`/asset-owners/${id}`).then((r) => r.data),
  reactivate: (id) =>
    api.post(`/asset-owners/${id}/reactivate`).then((r) => r.data),
  linkContact: (id, contact_id) =>
    api
      .post(`/asset-owners/${id}/contacts`, { contact_id })
      .then((r) => r.data),
  unlinkContact: (id, relationship_id) =>
    api
      .delete(`/asset-owners/${id}/contacts/${relationship_id}`)
      .then((r) => r.data),
};
